import type { PropsWithChildren } from 'react'
import React, { useCallback } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { accountAccessShowLogin } from 'actions/UiActions'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextButton from 'LuxKit/Button/TextButton'
import { hasPassedWalledGarden } from 'selectors/accountSelectors'
import LineTagAltIcon from 'LuxKit/Icons/line/LineTagAltIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { useIntl } from 'react-intl'

const Root = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const BottomWrapper = styled.div`
  display: flex;
  margin-top: ${rem(12)};
  justify-content: flex-end;

  ${mediaQueryUp.desktop} {
    margin-top: ${rem(16)};
  }

  > * + * {
    margin-left: ${rem(8)};
  }

  &.align-left:not(.full-width) {
    justify-content: flex-start;
  }

  &.align-center:not(.full-width) {
    justify-content: center;
  }

  &.align-right:not(.full-width) {
    justify-content: flex-end;
  }
`

const BtnSignUp = styled(TextButton)`
  min-width: ${rem(100)};

  &.full-width {
    flex: 1;
  }

  &.md-full-width {
    ${mediaQueryUp.desktop} {
      flex: 1;
    }
  }
`

interface Props {
  showContent?: boolean
  loginText?: string
  signUpText?: string
  discountPercentage?: number
  align?: 'left' | 'center' | 'right' | 'none'
  fullWidth?: boolean
  discountLabelAlign?: 'start' | 'end'
  className?: string
  mdFullWidth?: boolean
}

function OfferTileLoggedInButtons({
  showContent,
  children,
  signUpText,
  discountPercentage,
  align = 'center',
  fullWidth = true,
  discountLabelAlign,
  className,
  mdFullWidth,
}: PropsWithChildren<Props>) {
  const intl = useIntl()
  const dispatch = useAppDispatch()

  const showLogin = useCallback<React.MouseEventHandler<HTMLButtonElement>>(e => {
    e.preventDefault()
    e.stopPropagation()
    dispatch(accountAccessShowLogin())
  }, [dispatch])

  if (showContent) {
    return <>{children}</>
  }

  const alignClass = `align-${align}`
  const signUpToAccessLabel = intl.formatMessage({
    id: 'QA8D46Q4TY5bAGJInU2z',
    defaultMessage: 'Sign up to access exclusive rates',
    description: 'Prompt text encouraging users to sign up to access exclusive member rates',
  })
  const discountLabel = discountPercentage
    ? intl.formatMessage(
        {
          id: '1oFLGMUjsInblbVHkvgl',
          defaultMessage: 'Get {discountPercentage}% off with member prices',
          description: 'Discount label showing the percentage saving available with member pricing',
        },
        { discountPercentage },
      )
    : signUpToAccessLabel
  return <Root className={className}>
    <BodyTextBlock
      colour="neutral-one"
      variant="medium"
      weight="bold"
      startIcon={<LineTagAltIcon />}
      align={discountLabelAlign}
    >
      {discountLabel}
    </BodyTextBlock>
    <BottomWrapper className={clsx({ 'full-width': fullWidth, [alignClass]: !fullWidth })}>
      <BtnSignUp
        kind="secondary"
        className={clsx({ 'full-width': fullWidth, 'md-full-width': mdFullWidth })}
        onClick={showLogin}
      >
        {signUpText ?? intl.formatMessage({
          id: 'GKeXbLwk2ahwqk4WE2ji',
          defaultMessage: 'Sign up for free',
          description: 'Default CTA button text to sign up for a free account',
        })}
      </BtnSignUp>
    </BottomWrapper>
  </Root>
}

const mapStateToProps = (state: App.State, props: Props) => ({
  showContent: hasPassedWalledGarden(state) || props.showContent,
})

export default connect(mapStateToProps)(OfferTileLoggedInButtons)
